import type { DockviewApi } from "dockview-react";
import {
  CommandRegistry,
  activeWorkspace,
  addWorkspace,
  duplicateWorkspace,
  resetWorkspace,
  setActiveWorkspace,
  setWorkspaceLocked,
  useSettings,
  visualStylePatch,
  toggleAppearanceTheme,
  type Command,
  type LinkGroup,
} from "@helm/core";
import { TRADING_ACTIONS } from "@helm/ui";
import { registerChartCommands } from "../chart/commands";
import { chartModuleCount, openChartLayoutPicker } from "../chart/chartModules";

import { openBracketEditor } from "../brackets/bracketStore";
import { openSidebarTab } from "../sidebar/events";
import { settingsSync } from "../session";
import { signOut } from "../auth/signOut";
import { openSettingsDialog } from "../settings/settingsDialog";
import { requestSettingsTab } from "../settings/settingsTabRequest";
import { openCopyTrader, openRithmicDialog } from "../topbar/topbarDialogs";
import { workspaceActions } from "../workspace/Workspace";
import { TEMPLATES, WIDGET_TITLES, type WidgetKind } from "../workspace/templates";
import { newWorkspaceId, type WorkspaceStore } from "../workspace/workspaceStore";
import { toggleChartOnly } from "../workspace/chartOnly";

/**
 * The desktop command registry (SPEC §17.5): every action in one place, for the palette and
 * hotkeys. Trading actions are registered with `when: () => false` — unavailable until the order
 * core exists (A2) — and their `run` is a no-op, so neither the palette nor a hotkey can trade.
 */
export function buildRegistry(deps: {
  dock: () => DockviewApi | null;
  workspaces: WorkspaceStore;
  setSymbol: (group: LinkGroup, symbol: string) => void;
  openLockout: () => void;
}): { registry: CommandRegistry; tradingCommands: Command[] } {
  const r = new CommandRegistry();
  const withDock = (f: (d: DockviewApi) => void) => () => {
    const d = deps.dock();
    if (d) f(d);
  };

  for (const kind of Object.keys(WIDGET_TITLES) as WidgetKind[]) {
    r.register({
      id: `workspace.add.${kind}`,
      title: `Add module: ${WIDGET_TITLES[kind]}`,
      group: "workspace",
      keywords: ["widget", "panel", "module", kind],
      run: withDock((d) => {
        workspaceActions.addModule(d, kind);
      }),
    });
  }
  const ws = deps.workspaces;
  const activeId = (): string => activeWorkspace(ws.doc).id;
  for (const t of TEMPLATES) {
    r.register({
      id: `workspace.template.${t.id}`,
      title: `Reset workspace to template: ${t.name}`,
      group: "workspace",
      keywords: ["layout", "template", "reset"],
      run: () => {
        ws.update((d) => resetWorkspace(d, activeId(), t.id), { rebuild: true });
      },
    });
    r.register({
      id: `workspace.new.${t.id}`,
      title: `New workspace: ${t.name}`,
      group: "workspace",
      keywords: ["layout", "template", "tab", "new"],
      run: () => {
        ws.update((d) => addWorkspace(d, { id: newWorkspaceId(), template: t.id, name: t.name }));
      },
    });
  }
  r.register({
    id: "workspace.reset",
    title: "Reset workspace layout (to its template)",
    group: "workspace",
    run: () => {
      ws.update((d) => resetWorkspace(d, activeId()), { rebuild: true });
    },
  });
  r.register({
    id: "workspace.duplicate",
    title: "Duplicate workspace",
    group: "workspace",
    keywords: ["copy", "layout", "tab"],
    run: () => {
      ws.update((d) => duplicateWorkspace(d, activeId(), newWorkspaceId()));
    },
  });
  r.register({
    id: "workspace.lock",
    title: "Lock / unlock layout",
    group: "workspace",
    keywords: ["lock", "freeze", "drag"],
    run: () => {
      const w = activeWorkspace(ws.doc);
      ws.update((d) => setWorkspaceLocked(d, w.id, !w.locked));
    },
  });
  for (let n = 1; n <= 9; n++) {
    r.register({
      id: `workspace.switch.${String(n)}`,
      title: `Switch to workspace ${String(n)}`,
      group: "workspace",
      hotkey: `Ctrl+${String(n)}`,
      keywords: ["tab", "layout"],
      when: () => ws.doc.workspaces.length >= n,
      run: () => {
        const w = ws.doc.workspaces[n - 1];
        if (w) ws.update((d) => setActiveWorkspace(d, w.id));
      },
    });
  }
  // Chart only (owner 2026-10-05): the status line's "Chart only" switch.
  r.register({
    id: "workspace.chartOnly",
    title: "Chart only: on/off",
    group: "workspace",
    keywords: ["chart", "only", "hide", "focus", "full", "dom", "accounts", "view"],
    run: () => {
      toggleChartOnly();
    },
  });
  r.register({
    id: "journal.open",
    title: "Open Daily Trading journal",
    group: "workspace",
    keywords: ["journal", "checklist", "notes", "prep", "grade"],
    run: withDock((d) => {
      workspaceActions.focusOrAdd(d, "journal");
    }),
  });
  for (const sym of ["NQ", "MNQ", "ES", "MES"]) {
    r.register({
      id: `symbol.${sym.toLowerCase()}`,
      title: `Symbol: ${sym} (red group)`,
      group: "navigation",
      keywords: ["retarget", sym.toLowerCase()],
      run: () => {
        // Front-month resolution (SPEC §6.6) needs the ticker plant; the root is shown until then.
        deps.setSymbol("red", sym);
      },
    });
  }
  // History reload / clear-cache commands (SPEC-ADDENDA A1).
  registerChartCommands(r);
  r.register({
    id: "chart.layout",
    title: "Chart: Change layout…",
    group: "chart",
    keywords: ["split", "grid", "multi", "layout", "sync", "panes", "2x2"],
    when: () => chartModuleCount() > 0,
    run: () => {
      openChartLayoutPicker();
    },
  });
  r.register({
    id: "settings.theme.toggle",
    title: "Toggle light/dark theme",
    group: "settings",
    run: () => {
      void settingsSync.update(toggleAppearanceTheme(useSettings.getState().settings));
    },
  });
  // Visual style (owner 2026-09-29): Tradovate (default), Tradesea or Glacis; synced like every
  // setting, and marked as the user's choice.
  for (const [style, name] of [
    ["tradovate", "Classic (Tradovate)"],
    ["pro", "Pro"],
    ["terminal", "Terminal"],
    ["focus", "Focus (compact chrome)"],
    ["studio", "Studio"],
    ["tradesea", "Tradesea"],
    ["glacis", "Glacis"],
  ] as const) {
    r.register({
      id: `settings.style.${style}`,
      title: `Visual style: ${name}`,
      group: "settings",
      keywords: ["theme", "look", "appearance", "style", "mmt", style],
      run: () => {
        void settingsSync.update(visualStylePatch(style));
      },
    });
  }
  // The top-bar account figures (chips / compact strip) became the one position strip (owner
  // 2026-10-04 UI clean-up), so their palette toggle is gone; the setting is kept for old documents.
  r.register({
    id: "settings.topToolbar.toggle",
    title: "Toggle the top toolbar (active chart: symbol, timeframes, indicators, Flatten…)",
    group: "settings",
    keywords: ["appearance", "toolbar", "glacis", "timeframe"],
    run: () => {
      void settingsSync.update({ top_toolbar: !useSettings.getState().settings.top_toolbar });
    },
  });
  for (const [tab, name] of [
    ["watchlist", "Watchlist"],
    ["details", "Details"],
    ["alerts", "Alerts"],
  ] as const) {
    r.register({
      id: `sidebar.${tab}`,
      title: `Right sidebar: ${name}`,
      group: "settings",
      keywords: ["sidebar", "right", "panel", tab],
      run: () => {
        openSidebarTab(tab);
      },
    });
  }
  r.register({
    id: "settings.privacy.toggle",
    title: "Toggle privacy mask",
    group: "settings",
    keywords: ["hide", "mask", "balance", "***"],
    run: () => {
      void settingsSync.update({ privacy: !useSettings.getState().settings.privacy });
    },
  });
  // Settings is a window-wide dialog, never a dock module (owner 2026-10-04).
  r.register({
    id: "settings.open",
    title: "Open settings",
    group: "settings",
    keywords: ["preferences", "theme", "palette"],
    run: () => {
      openSettingsDialog();
    },
  });
  r.register({
    id: "settings.risk",
    title: "Risk settings (daily max loss, take profit, trailing limit)",
    group: "settings",
    keywords: ["pdll", "pdpt", "limit", "trail", "risk"],
    run: () => {
      requestSettingsTab("risk");
      openSettingsDialog();
    },
  });
  r.register({
    id: "copy.open",
    title: "Copy trader…",
    group: "settings",
    keywords: ["copy", "leader", "follower", "mirror", "group"],
    run: () => {
      openCopyTrader();
    },
  });
  r.register({
    id: "rithmic.login",
    title: "Log in to Rithmic…",
    group: "settings",
    keywords: ["rithmic", "connect", "login", "account"],
    run: () => {
      openRithmicDialog("login");
    },
  });
  r.register({
    id: "rithmic.add",
    title: "Add Rithmic account…",
    group: "settings",
    keywords: ["rithmic", "add", "login", "account", "connect"],
    run: () => {
      openRithmicDialog("add");
    },
  });
  r.register({
    id: "brackets.edit",
    title: "Bracket strategies… (TP/SL presets)",
    group: "settings",
    keywords: [
      "bracket",
      "atm",
      "oco",
      "take profit",
      "stop loss",
      "preset",
      "scale out",
      "breakeven",
      "trail",
    ],
    run: () => {
      openBracketEditor();
    },
  });
  r.register({
    id: "risk.lockout",
    title: "Personal Lockout…",
    group: "settings",
    keywords: ["lock", "lockout", "break", "stop trading"],
    run: deps.openLockout,
  });
  r.register({
    id: "session.signout",
    title: "Sign out",
    group: "session",
    run: () => signOut(),
  });

  const tradingCommands: Command[] = TRADING_ACTIONS.map((a) => ({
    id: a.id,
    title: a.title,
    group: "trading",
    hotkey: a.hotkey,
    keywords: [a.semantics, "trade", "order"],
    when: () => false,
    run: () => {},
  }));
  for (const c of tradingCommands) r.register(c);
  return { registry: r, tradingCommands };
}
